import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/styles';
import {
  TextField,
  Popper,
  Paper,
  MenuItem,
  Typography,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
} from '@material-ui/core';

import { axiosInstance } from 'common/network';

const useStyles = makeStyles(theme => ({
  popper: {
    zIndex: theme.zIndex.modal,
  },
  paper: {
    maxHeight: 320,
    overflowY: 'auto',
    marginTop: theme.spacing(0.5),
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    whiteSpace: 'normal',
  },
  optionSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  message: {
    padding: theme.spacing(1, 2),
  },
}));

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

// Lightweight account picker built on core components (no @material-ui/lab): debounced free-text
// search against /v3/accounts/search, resolving the chosen account to its UUID for the caller.
// A picked account stays selected until the text is edited again.
const AccountAutocomplete = props => {
  const { label, selected, onSelect } = props;
  const classes = useStyles();

  const anchorRef = useRef(null);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  const [input, setInput] = useState(selected ? selected.name || selected.email || '' : '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const runSearch = term => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    axiosInstance
      .get(process.env.REACT_APP_API_ROOT + 'v3/accounts/search', { params: { q: term, limit: 10 } })
      .then(response => {
        if (seq !== requestSeq.current) return;
        setOptions(Array.isArray(response.data) ? response.data : []);
        setLoading(false);
        setOpen(true);
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setOptions([]);
        setLoading(false);
        setOpen(true);
      });
  };

  const handleInput = event => {
    const text = event.target.value;
    setInput(text);
    // Editing the text drops any previously resolved account until a new one is picked.
    if (selected) onSelect(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const term = text.trim();
    if (term.length < MIN_CHARS) {
      requestSeq.current += 1; // discard any in-flight response
      setOptions([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(term), DEBOUNCE_MS);
  };

  const pick = option => {
    onSelect(option);
    setInput(option.name || option.email || '');
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div ref={anchorRef}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label={label}
          value={input}
          onChange={handleInput}
          onFocus={() => { if (options.length) setOpen(true); }}
          InputProps={{
            endAdornment: loading ? (
              <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
            ) : null,
          }}
        />
        <Popper
          className={classes.popper}
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          disablePortal
          style={{ width: anchorRef.current ? anchorRef.current.clientWidth : undefined }}
        >
          <Paper className={classes.paper}>
            {options.length === 0 ? (
              <Typography className={classes.message} variant="body2" color="textSecondary">
                {loading ? 'Searching…' : 'No matching accounts.'}
              </Typography>
            ) : (
              options.map(option => (
                <MenuItem
                  key={option.accountUuid}
                  className={classes.option}
                  onClick={() => pick(option)}
                >
                  <span>{option.name || option.email || option.accountUuid}</span>
                  {option.name && option.email && (
                    <span className={classes.optionSecondary}>{option.email}</span>
                  )}
                </MenuItem>
              ))
            )}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

AccountAutocomplete.propTypes = {
  label: PropTypes.string,
  selected: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
};

export default AccountAutocomplete;
